import axios from 'axios';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { cexExchangesTimeframes, IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';
import { trimDecimalZeroes } from 'utils/formats/trimDecimalZeroes';
import { ECexName } from 'web3';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { bootstrapStrategy } from '../bootstrap';
import { aggregateOrderbook, mergeOrderBook, sortOrderbookFunc } from '../utils';

export class BinanceStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  depthWs: ReconnectingWebSocket | undefined;
  klineWs: ReconnectingWebSocket | undefined;
  tickerWs: ReconnectingWebSocket | undefined;
  tradeWs: ReconnectingWebSocket | undefined;
  bids: [string, string][];
  asks: [string, string][];
  pair: string;
  displayDepth: number; // User requested depth (20 or 50)
  fetchDepth = 1000; // Always fetch more data
  precision: number;

  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  selectedPrecision: number;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toUpperCase()}${quote.toUpperCase()}`;
    this.bids = [];
    this.asks = [];
    this.displayDepth = orderbookDepth ?? 20;
    this.precision = 1;
    this.selectedPrecision = 1;

    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  fetchTickerPrecision = async () => {
    try {
      const res = await axios.get<{
        symbols: {
          symbol: string;
          filters: {
            filterType: string;
            tickSize: string;
          }[];
        }[];
      }>(`https://api.binance.com/api/v3/exchangeInfo?symbol=${this.pair}`);

      let prec = 1;

      if (res.data) {
        const symbol = res.data.symbols.find(el => el.symbol === this.pair);

        if (symbol) {
          const filter = symbol.filters.find(el => el.filterType === 'PRICE_FILTER');

          if (filter) {
            const num = trimDecimalZeroes(filter.tickSize);

            prec = num.split('.')[1].length;
          }
        }
      }

      this.precision = prec;
      this.selectedPrecision = prec;
      this.precisionUpdate(this.precision);
    } catch (error) {
      console.log('Binance ticker error: ', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const res = await axios.get<{ asks: [string, string][]; bids: [string, string][] }>(
        `https://api.binance.com/api/v3/depth?symbol=${this.pair}&limit=${this.fetchDepth}`,
      );

      if (res.data) {
        this.asks = res.data.asks;
        this.bids = res.data.bids;

        // Apply precision and filter to display depth
        const orderbook = aggregateOrderbook(
          res.data.asks,
          res.data.bids,
          this.selectedPrecision,
          this.displayDepth,
        );

        this.onOrderbookUpdate(orderbook);

        return orderbook;
      }
    } catch (error) {
      console.log('Binance orderbook error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    limit: number;
    interval: IChartTimeframe;
  }) => {
    const innerInterval = cexExchangesTimeframes[interval][ECexName.binance];

    try {
      const result = await axios.get<string[]>('https://api.binance.com/api/v3/klines', {
        params: {
          symbol: `${base.toUpperCase()}${quote.toUpperCase()}`,
          limit,
          interval: innerInterval,
        },
      });

      return (result.data ?? []).map(el => [
        Number(el[0]),
        el[1],
        el[2],
        el[3],
        el[4],
        el[5],
      ]) as KLine[];
    } catch (error) {
      console.log('Binance klines error: ', error);

      return [];
    }
  };

  fetchTrades = async ({ base, quote, limit }: { base: string; quote: string; limit: number }) => {
    try {
      const result = await axios.get<
        {
          isBuyerMaker: boolean;
          price: string;
          qty: string;
          time: string;
        }[]
      >('https://api.binance.com/api/v3/trades', {
        params: {
          symbol: `${base.toUpperCase()}${quote.toUpperCase()}`,
          limit,
        },
      });

      return (
        result.data?.map(item => ({
          price: item.price,
          quantity: item.qty,
          timestamp: item.time,
          side: item.isBuyerMaker ? 'sell' : 'buy',
        })) ?? []
      );
    } catch (error) {
      console.log('Binance trades error: ', error);
      return [];
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    this.closeWebSocket();
    const intervalChannel = {
      60000: '1m',
      300000: '5m',
      900000: '15m',
      3600000: '1h',
      14400000: '4h',
      86400000: '1d',
      604800000: '1w',
    }[interval];

    const depthSocketUrl = `wss://stream.binance.com:9443/ws/${this.pair.toLowerCase()}@depth`;
    const klineSocketUrl = `wss://stream.binance.com:9443/ws/${this.pair.toLowerCase()}@kline_${intervalChannel}`;
    const tickerSocketUrl = `wss://stream.binance.com:9443/ws/${this.pair.toLowerCase()}@ticker`;
    const tradeSocketUrl = `wss://stream.binance.com:9443/ws/${this.pair.toLowerCase()}@trade`;

    const depthWs = new ReconnectingWebSocket(depthSocketUrl, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    const klineWs = new ReconnectingWebSocket(klineSocketUrl, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    const tickerWs = new ReconnectingWebSocket(tickerSocketUrl, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    const tradeWs = new ReconnectingWebSocket(tradeSocketUrl, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    this.depthWs = depthWs;
    this.klineWs = klineWs;
    this.tickerWs = tickerWs;
    this.tradeWs = tradeWs;

    this.depthWs.onmessage = event => {
      try {
        const data = JSON.parse(event.data);

        const asks = mergeOrderBook(this.asks, data.a)
          .sort(sortOrderbookFunc)
          .slice(0, this.fetchDepth);
        const bids = mergeOrderBook(this.bids, data.b)
          .sort(sortOrderbookFunc)
          .slice(-this.fetchDepth);

        this.bids = bids;
        this.asks = asks;

        const orderbook = aggregateOrderbook(
          asks.map(el => [el[0], el[1]]),
          bids.map(el => [el[0], el[1]]),
          this.selectedPrecision,
          this.displayDepth,
        );

        this.onOrderbookUpdate(orderbook);
      } catch (error) {
        console.log('Binance depth socket error', error);
      }
    };

    this.klineWs.onmessage = event => {
      try {
        const data = JSON.parse(event.data);
        const { t, o, h, l, c, v } = data.k;
        this.klineUpdate([Math.floor(Number(t) / 1000), o, h, l, c, v]);
      } catch (error) {
        console.log('Binance kline socket error', error);
      }
    };

    this.tickerWs.onmessage = event => {
      try {
        const data = JSON.parse(event.data);
        const { P, v, q, c, h, l } = data;
        this.onTickerUpdate({
          baseVolume: v,
          change: P,
          close: c,
          high: h,
          last: Number(c).toFixed(this.precision),
          low: l,
          quoteVolume: q,
        });
      } catch (error) {
        console.log('Binance ticker socket error', error);
      }
    };

    this.tradeWs.onmessage = event => {
      try {
        const data = JSON.parse(event.data);
        const { p, q, T, m } = data;

        this.onTradeUpdate({
          price: p,
          quantity: q,
          timestamp: T,
          side: m ? 'sell' : 'buy',
        });
      } catch (error) {
        console.log('Binance trade socket error', error);
      }
    };
  };

  closeWebSocket() {
    if (this.depthWs) this.depthWs.close();
    if (this.klineWs) this.klineWs.close();
    if (this.tickerWs) this.tickerWs.close();
    if (this.tradeWs) this.tradeWs.close();
  }

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;

    const orderbook = aggregateOrderbook(
      this.asks,
      this.bids,
      this.selectedPrecision,
      this.displayDepth,
    );
    this.onOrderbookUpdate(orderbook);
  };

  setOrderbookDepth(depth: number) {
    this.displayDepth = depth;
    this.fetchOrderbook();
  }

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
    await this.fetchOrderbook();
  };

  static init = async (args: IInitArgs) => {
    const instance = new BinanceStrategy(args);

    await bootstrapStrategy(instance);

    return instance;
  };
}
